import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { ACTIVITIES, ActivityType, getActivityTypeIcon } from "@/lib/builder/models/activity-configs.model";
import { LibraryActivitiesTable } from "@/lib/library/LibraryActivitiesTable";
import { LibraryArchiveSelector, LibraryArchiveScope } from "@/lib/library/LibraryArchiveSelector";
import { useActivityLibraryQuery } from "@/lib/library/hooks/useActivityLibrary";

/**
 * Pagina "Libreria": una singola vista con un filtro a icone per tipo attività
 * (accommodation, activity, flight, transportation, food-drinks, events, notes),
 * riusando la stessa config `ACTIVITIES` del builder per label/icone così la
 * libreria resta coerente con il linguaggio visivo già noto agli utenti.
 * Il filtro è multi-select (nessuna icona selezionata = mostra tutti i tipi),
 * coerente col filtro tag già esistente in `LibraryActivitiesTable`.
 *
 * Una singola fetch (`useActivityLibraryQuery()` senza filtri) recupera tutte le
 * righe visibili all'utente (la RLS su `activity_library` già limita la visibilità
 * a user/agency/admin): tipo attività, tag e ricerca testuale sono poi tutti filtri
 * client-side, per evitare una chiamata di rete per ogni cambio filtro o carattere digitato.
 *
 * Chi ha un'agenzia (account `agency` stesso, o `travel_advisor_member` con `agency_id`)
 * sceglie prima se navigare la libreria personale o quella dell'agenzia
 * (`LibraryArchiveSelector`); chi non ha agenzia salta lo step (scope fisso 'user');
 * l'admin salta lo step e mantiene il comportamento attuale (vede tutto, con il filtro
 * ownerScope già esistente in `LibraryActivitiesTable`).
 */
const LibraryPage = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userData, userRole } = useAuth();

  const isAdmin = userRole === 'admin';
  // Per un account `agency` la libreria "dell'agenzia" è la propria (owner_id = proprio id);
  // per un `travel_advisor_member` è quella dell'agenzia a cui è collegato (`userData.agency_id`).
  const agencyId = userRole === 'agency' ? user?.id ?? null : userData?.agency_id ?? null;
  const hasAgency = !isAdmin && !!agencyId;

  const { data: activities = [], isLoading } = useActivityLibraryQuery();

  const scopeParam = searchParams.get('scope');
  const scope: LibraryArchiveScope | null = hasAgency
    ? scopeParam === 'agency' || scopeParam === 'user'
      ? scopeParam
      : null
    : null;

  const needsArchiveChoice = hasAgency && !scope;

  const scopedActivities = useMemo(() => {
    // Admin: nessun filtro owner qui, vede tutto (il filtro ownerScope resta dentro
    // LibraryActivitiesTable, com'era già).
    if (isAdmin) return activities;
    // Nessuna agenzia: scope fisso sulla libreria personale.
    if (!hasAgency) return activities.filter((a) => a.owner_type === 'user' && a.owner_id === user?.id);
    // Ha un'agenzia ma non ha ancora scelto lo scope: non renderizziamo la tabella in questo
    // caso (vedi `needsArchiveChoice`), l'array vuoto è solo un valore placeholder sicuro.
    if (!scope) return [];
    if (scope === 'user') return activities.filter((a) => a.owner_type === 'user' && a.owner_id === user?.id);
    return activities.filter((a) => a.owner_type === 'agency' && a.owner_id === agencyId);
  }, [activities, isAdmin, hasAgency, scope, user?.id, agencyId]);

  // `accommodationOut` è una variante interna nascosta nel builder (stesso modal
  // di accommodation): non ha senso come filtro separato in libreria.
  const visibleActivityConfigs = useMemo(
    () => ACTIVITIES.filter((activityConfig) => !activityConfig.hidden),
    []
  );

  const typesParam = searchParams.get('types');
  const selectedActivityTypes: ActivityType[] = useMemo(() => {
    if (!typesParam) return [];
    const validIds = new Set(visibleActivityConfigs.map((activityConfig) => activityConfig.id));
    return typesParam
      .split(',')
      .filter((value): value is ActivityType => validIds.has(value as ActivityType));
  }, [typesParam, visibleActivityConfigs]);

  const handleToggleActivityType = (activityType: ActivityType) => {
    const next = new URLSearchParams(searchParams);
    const nextSelected = selectedActivityTypes.includes(activityType)
      ? selectedActivityTypes.filter((value) => value !== activityType)
      : [...selectedActivityTypes, activityType];

    if (nextSelected.length === 0) {
      next.delete('types');
    } else {
      next.set('types', nextSelected.join(','));
    }
    setSearchParams(next);
  };

  const handleScopeSelect = (nextScope: LibraryArchiveScope) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", nextScope);
    setSearchParams(next);
  };

  const handleChangeArchive = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("scope");
    setSearchParams(next);
  };

  return (
    <PrivateLayout pageTitle={t("library.title")} pageDescription={t("library.pageDescription")}>
      <div className="space-y-6 mb-12">
        {needsArchiveChoice ? (
          <LibraryArchiveSelector onSelect={handleScopeSelect} />
        ) : (
          <>
            {hasAgency && (
              <div className="max-w-6xl mx-auto flex justify-end">
                <button
                  type="button"
                  onClick={handleChangeArchive}
                  className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
                >
                  {t('library.changeArchiveAction')}
                </button>
              </div>
            )}

            <div className="max-w-6xl mx-auto space-y-4">
              <TooltipProvider>
                <div className="flex flex-wrap gap-2">
                  {visibleActivityConfigs.map((activityConfig) => {
                    const isActive = selectedActivityTypes.includes(activityConfig.id);
                    // `labelKey` è tipizzato `string` in ActivityTypeConfig (usato da builder generico);
                    // stesso cast già adottato da DayBuilderStep.tsx/TimelineCard.tsx per lo stesso campo.
                    const label = t(activityConfig.labelKey as any); // eslint-disable-line @typescript-eslint/no-explicit-any
                    return (
                      <Tooltip key={activityConfig.id}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-pressed={isActive}
                            aria-label={label}
                            onClick={() => handleToggleActivityType(activityConfig.id)}
                            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              isActive
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {getActivityTypeIcon(activityConfig.id)}
                            <span>{label}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{label}</TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>

              <LibraryActivitiesTable
                selectedActivityTypes={selectedActivityTypes}
                activities={scopedActivities}
                isLoading={isLoading}
              />
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
};

export default LibraryPage;
